import React, { useState } from 'react';
import { Plus, RotateCcw, Square, Ruler, Target, Layers, Sigma, PanelLeft } from 'lucide-react';
import type { TableConfig } from '@/app/types/table';
import { useLanguage } from '@/app/contexts/LanguageContext';

export interface TableMainToolbarProps {
  tableConfig: TableConfig;
  headerCellSelection: Array<{ rowId: string; cellId: string }>;
  onMergeHeaderCells: () => void;
  onSplitHeaderCells: () => void;
  onClearSelection: () => void;
  onAddColumn?: () => void;
  onAddHeaderRow: () => void;
  onResetHeaderRows: () => void;
  onApplyBorderPreset: (preset: string) => void;
  onOpenBorderDialog: () => void;
  onOpenPrintDialog?: () => void; // Kept in interface but unused in desktop view
  onOpenGroupByPanel?: () => void;
  onToggleTotalsRow?: () => void;
  onToggleSidebar?: () => void;
  isSidebarVisible?: boolean;
}

type BorderPreset = 
  | 'outer-thick' 
  | 'all-borders' 
  | 'no-borders' 
  | 'horizontal-only' 
  | 'vertical-only'
  | 'inner-grid';

export function TableMainToolbarDesktop(props: TableMainToolbarProps) {
  const { t } = useLanguage();
  const {
    tableConfig,
    headerCellSelection,
    onMergeHeaderCells,
    onSplitHeaderCells,
    onClearSelection,
    onAddColumn,
    onAddHeaderRow,
    onResetHeaderRows,
    onApplyBorderPreset,
    onOpenBorderDialog,
    // onOpenPrintDialog unused here (moved to top toolbar)
    onOpenGroupByPanel,
    onToggleTotalsRow,
    onToggleSidebar,
    isSidebarVisible,
  } = props;

  const [showBorderMenu, setShowBorderMenu] = useState(false);
  const [showLineMenu, setShowLineMenu] = useState(false);
  
  const hasSelection = headerCellSelection.length > 0;
  const hasMultipleSelected = headerCellSelection.length >= 2;
  const hasSingleSelected = headerCellSelection.length === 1;

  const borderPresets: Array<{ id: BorderPreset; label: string; icon: string }> = [
    { id: 'outer-thick', label: t('table.toolbar.borderPresetsNames.outerThick'), icon: '⬜' },
    { id: 'all-borders', label: t('table.toolbar.borderPresetsNames.allBorders'), icon: '⊞' },
    { id: 'no-borders', label: t('table.toolbar.borderPresetsNames.noBorders'), icon: '⊟' },
    { id: 'horizontal-only', label: t('table.toolbar.borderPresetsNames.horizontalOnly'), icon: '☰' },
    { id: 'vertical-only', label: t('table.toolbar.borderPresetsNames.verticalOnly'), icon: '☱' },
    { id: 'inner-grid', label: t('table.toolbar.borderPresetsNames.innerGrid'), icon: '⊡' },
  ];
  
  const lineWidths = [
    { value: 1, label: `${t('table.toolbar.thickness')} 1px` },
    { value: 2, label: `${t('table.toolbar.thickness')} 2px`, recommended: true },
    { value: 3, label: `${t('table.toolbar.thickness')} 3px` },
    { value: 4, label: `${t('table.toolbar.thickness')} 4px` },
  ];

  return (
    <div 
      className="hidden 2xl:flex flex-col border-b"
      style={{
        backgroundColor: 'var(--md-surface-container)',
        borderColor: 'var(--md-outline)',
      }}
    >
      {/* SINGLE ROW: All table-related functions */}
      <div 
        className="flex items-start gap-4 px-4 py-2" 
        style={{ borderColor: 'var(--md-outline-variant)' }}
      >
        {/* Border Operations Group */}
        <div className="flex flex-col items-start gap-1">
          <div className="flex items-center gap-2">
            {/* Border Preset Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowBorderMenu(!showBorderMenu)}
                disabled={!hasSelection}
                className="px-3 h-8 text-sm rounded transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
                style={{
                  backgroundColor: 'var(--md-surface-container-high)',
                  color: 'var(--md-on-surface)',
                  border: '1px solid var(--md-outline)',
                }}
                title={t('table.toolbar.borderPresets')}
              >
                <Square className="w-4 h-4" />
                {t('table.toolbar.borders')} ▼
              </button>
              
              {showBorderMenu && hasSelection && (
                <div 
                  className="absolute top-full left-0 mt-1 py-1 rounded shadow-lg z-50 min-w-[200px]"
                  style={{
                    backgroundColor: 'var(--md-surface-container-high)',
                    border: '1px solid var(--md-outline)',
                  }}
                >
                  {borderPresets.map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => {
                        onApplyBorderPreset(preset.id);
                        setShowBorderMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:opacity-90 transition-colors flex items-center gap-3"
                      style={{
                        color: 'var(--md-on-surface)',
                        backgroundColor: 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--md-surface-container-highest)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <span className="text-lg">{preset.icon}</span>
                      <span>{preset.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Line Width Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowLineMenu(!showLineMenu)}
                disabled={!hasSelection}
                className="px-3 h-8 text-sm rounded transition-colors flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
                style={{
                  backgroundColor: 'var(--md-surface-container-high)',
                  color: 'var(--md-on-surface)',
                  border: '1px solid var(--md-outline)',
                }}
                title={t('table.toolbar.lineWidth')}
              >
                <Ruler className="w-4 h-4" />
                {t('table.toolbar.thickness')} ▼
              </button>
              
              {showLineMenu && hasSelection && (
                <div 
                  className="absolute top-full left-0 mt-1 py-1 rounded shadow-lg z-50 min-w-[180px]"
                  style={{
                    backgroundColor: 'var(--md-surface-container-high)',
                    border: '1px solid var(--md-outline)',
                  }}
                >
                  {lineWidths.map((width) => (
                    <button
                      key={width.value}
                      onClick={() => {
                        onApplyBorderPreset(`width-${width.value}`);
                        setShowLineMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm hover:opacity-90 transition-colors flex items-center gap-3"
                      style={{
                        color: 'var(--md-on-surface)',
                        backgroundColor: 'transparent',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--md-surface-container-highest)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <div 
                        style={{ 
                          width: '40px', 
                          height: `${width.value}px`, 
                          backgroundColor: 'currentColor',
                          borderRadius: '1px',
                        }} 
                      />
                      <span>
                        {width.label}
                        {width.recommended && <span className="ml-2 text-xs">⭐</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Bereiche Button (Main Feature) */}
            <button
              onClick={onOpenBorderDialog}
              className="px-3 h-8 text-sm rounded font-medium transition-colors flex items-center justify-center gap-2 hover:opacity-90"
              style={{
                backgroundColor: 'var(--md-primary)',
                color: 'var(--md-on-primary)',
                border: '1px solid transparent',
              }}
              title={t('table.toolbar.editBorders')}
            >
              <Target className="w-4 h-4" />
              {t('table.toolbar.areas')}
            </button>
          </div>
          <span className="text-xs font-medium uppercase tracking-wide px-1" style={{ color: 'var(--md-on-surface-variant)' }}>
            {t('table.toolbar.borders')}
          </span>
        </div>

        {/* Divider */}
        <div className="h-10 w-px self-center" style={{ backgroundColor: 'var(--md-outline-variant)' }} />

        {/* Cell Operations Group */}
        <div className="flex flex-col items-start gap-1">
          <div className="flex items-center gap-2">
            <button
              onClick={onMergeHeaderCells}
              disabled={!hasMultipleSelected}
              className="px-3 h-8 text-sm rounded transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
              style={{
                backgroundColor: hasMultipleSelected ? 'var(--md-primary)' : 'var(--md-surface-container-high)',
                color: hasMultipleSelected ? 'var(--md-on-primary)' : 'var(--md-on-surface)',
                border: '1px solid transparent',
              }}
              title={t('table.cells.minTwo')}
            >
              {t('table.toolbar.merge')}
            </button>
            <button
              onClick={onSplitHeaderCells}
              disabled={!hasSingleSelected}
              className="px-3 h-8 text-sm rounded transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90"
              style={{
                backgroundColor: hasSingleSelected ? 'var(--md-secondary)' : 'var(--md-surface-container-high)',
                color: hasSingleSelected ? 'var(--md-on-secondary)' : 'var(--md-on-surface)',
                border: '1px solid transparent',
              }}
              title={t('table.cells.singleMerged')}
            >
              {t('table.toolbar.split')}
            </button>
          </div>
          <span className="text-xs font-medium uppercase tracking-wide px-1" style={{ color: 'var(--md-on-surface-variant)' }}>
            {t('table.toolbar.cells')}
          </span>
        </div>

        {/* Divider */}
        <div className="h-10 w-px self-center" style={{ backgroundColor: 'var(--md-outline-variant)' }} />

        {/* Header Management Group (Table) */}
        <div className="flex flex-col items-start gap-1">
          <div className="flex items-center gap-2">
            {onAddColumn && (
              <button
                onClick={onAddColumn}
                className="px-3 h-8 text-sm rounded transition-colors flex items-center justify-center gap-2 hover:opacity-90"
                style={{
                  backgroundColor: 'var(--md-surface-container-high)',
                  color: 'var(--md-on-surface)',
                  border: '1px solid var(--md-outline)',
                }}
                title={t('table.sidebar.createAttribute')}
              >
                <Plus className="w-4 h-4" />
                {t('table.toolbar.column')}
              </button>
            )}
            <button
              onClick={onAddHeaderRow}
              className="px-3 h-8 text-sm rounded transition-colors flex items-center justify-center gap-2 hover:opacity-90"
              style={{
                backgroundColor: 'var(--md-surface-container-high)',
                color: 'var(--md-on-surface)',
                border: '1px solid var(--md-outline)',
              }}
              title={t('table.grid.addHeaderRow')}
            >
              <Plus className="w-4 h-4" />
              {t('table.toolbar.row')}
            </button>
            <button
              onClick={onResetHeaderRows}
              className="px-3 h-8 text-sm rounded transition-colors flex items-center justify-center gap-2 hover:opacity-90"
              style={{
                backgroundColor: 'var(--md-surface-container-high)',
                color: 'var(--md-on-surface)',
                border: '1px solid var(--md-outline)',
              }}
              title={t('table.toolbar.resetHeaders')}
            >
              <RotateCcw className="w-4 h-4" />
              {t('common.reset')}
            </button>
          </div>
          <span className="text-xs font-medium uppercase tracking-wide px-1" style={{ color: 'var(--md-on-surface-variant)' }}>
            {t('table.toolbar.table')}
          </span>
        </div>

        {/* Divider */}
        <div className="h-10 w-px self-center" style={{ backgroundColor: 'var(--md-outline-variant)' }} />

        {/* Grouping (Data) */}
        {onOpenGroupByPanel && (
          <div className="flex flex-col items-start gap-1">
            <div className="flex items-center gap-2">
              <button
                onClick={onOpenGroupByPanel}
                className="px-3 h-8 text-sm rounded font-medium transition-colors flex items-center justify-center gap-2 hover:opacity-90"
                style={{
                  backgroundColor: tableConfig.groupConfig?.enabled ? 'var(--md-tertiary)' : 'var(--md-surface-container-high)',
                  color: tableConfig.groupConfig?.enabled ? 'var(--md-on-tertiary)' : 'var(--md-on-surface)',
                  border: '1px solid var(--md-outline)',
                }}
                title={t('table.toolbar.group')}
              >
                <Layers className="w-4 h-4" />
                {t('table.toolbar.group')}
                {tableConfig.groupConfig?.enabled && tableConfig.groupConfig.columnIds.length > 0 && (
                  <span className="ml-1 text-xs">
                    ({tableConfig.groupConfig.columnIds.length})
                  </span>
                )}
              </button>
              
              {onToggleTotalsRow && (
                <button
                  onClick={onToggleTotalsRow}
                  className="px-3 h-8 text-sm rounded font-medium transition-colors flex items-center justify-center gap-2 hover:opacity-90"
                  style={{
                    backgroundColor: tableConfig.totalsRowConfig?.enabled ? 'var(--md-tertiary)' : 'var(--md-surface-container-high)',
                    color: tableConfig.totalsRowConfig?.enabled ? 'var(--md-on-tertiary)' : 'var(--md-on-surface)',
                    border: '1px solid var(--md-outline)',
                  }}
                  title={t('table.totals.activated')}
                >
                  <Sigma className="w-4 h-4" />
                  {t('table.toolbar.totalsRow')}
                </button>
              )}
            </div>
            <span className="text-xs font-medium uppercase tracking-wide px-1" style={{ color: 'var(--md-on-surface-variant)' }}>
              {t('table.toolbar.data')}
            </span>
          </div>
        )}

        {/* Selection Info & Clear (right-aligned) */}
        {hasSelection && (
          <>
            <div className="flex-1" />
            <div className="flex items-center gap-3 self-center">
              <span className="text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
                {headerCellSelection.length} {headerCellSelection.length > 1 ? t('table.cells.noSelection').replace('Keine Zellen', 'Zellen').replace('No cells', 'Cells') : t('table.cells.noSelection').replace('Keine Zellen', 'Zelle').replace('No cells', 'Cell')} {t('table.sidebar.used').replace('verwendet', 'selected')}
              </span>
              <button
                onClick={onClearSelection}
                className="px-3 h-8 text-sm rounded transition-colors flex items-center justify-center hover:opacity-80"
                style={{
                  backgroundColor: 'transparent',
                  color: 'var(--md-on-surface)',
                  border: '1px solid var(--md-outline)',
                }}
              >
                {t('table.toolbar.clearSelection')}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Stats Footer */}
      <div 
        className="flex items-center justify-between px-4 py-1.5 text-xs border-t"
        style={{
          backgroundColor: 'var(--md-surface-container-low)',
          borderColor: 'var(--md-outline-variant)',
          color: 'var(--md-on-surface-variant)',
        }}
      >
        <div className="flex items-center gap-4">
          <span>{tableConfig.rows.filter(r => !r.isHeader).length} {t('table.toolbar.dataRows')}</span>
          <span>•</span>
          <span>{tableConfig.columns.length} {t('table.toolbar.columns')}</span>
          {tableConfig.headerRows && tableConfig.headerRows.length > 0 && (
            <>
              <span>•</span>
              <span>{tableConfig.headerRows.length} {t('table.toolbar.headerRows')}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}